import React, { useRef, useState } from "react";
import {
  ArrowLeft,
  Bot,
  Check,
  CheckCircle2,
  FilePlus2,
  Info,
  Loader2,
  Trash2,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";
import { useTranslation } from "../../../hooks/useTranslation";
import { useBillingUsage } from "../../../hooks/useApi";
import { restanti } from "../../../utils/limitiPiano";
import { GRATUITO } from "../../billing/piani";

// Quante fonti di ogni tipo si aggiungono qui (design 4032:10795: 5/1/10).
// Sopra a questi vale il limite di fonti del piano, letto dall'API: in tutto
// non se ne aggiungono piu' di quante il piano ne lascia libere.
const LIMITS = { url: 5, sitemap: 1, file: 10 };
const MAX_FILE_BYTES = 50 * 1024 * 1024;
const MAX_INLINE_TEXT = 100000;

const normalizeUrl = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const isFileSource = (source) =>
  source.type === "pdf" || (source.type === "text" && source.fromFile);

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  return bytes >= 1024 * 1024
    ? `${Math.round(bytes / (1024 * 1024))}MB`
    : `${Math.max(1, Math.round(bytes / 1024))}KB`;
};

/**
 * Riga fonte aggiunta (design nodo 4041:24147): testo 14px medium,
 * divisore #CBD5E1, bidoncino rosso 16px senza box; per i file una
 * seconda riga muted "25MB - File PDF".
 */
const SourceRow = ({ name, subtitle, onRemove, t }) => (
  <div className="flex items-center gap-5 border-b border-[#CBD5E1] py-2">
    <div className="min-w-0 flex-1 py-1">
      <p className="truncate text-sm font-medium tracking-[0.07px] text-[#020617]">
        {name}
      </p>
      {subtitle && (
        <p className="mt-1 text-xs leading-4 text-muted-foreground">
          {subtitle}
        </p>
      )}
    </div>
    <button
      type="button"
      onClick={onRemove}
      className="shrink-0 text-destructive hover:text-red-700"
      aria-label={t("onboardingWizard.removeSource")}
    >
      <Trash2 className="h-4 w-4" />
    </button>
  </div>
);

/** Input inline con prefisso https:// e bottone Aggiungi (design sezioni URL/sitemap). */
const UrlAdder = ({ placeholder, disabled, onAdd }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");

  const submit = () => {
    const url = normalizeUrl(value);
    if (url.length <= "https://".length + 3) return;
    // false: non aggiunto (si sono aperti i piani), il link resta nel campo.
    if (onAdd(url) === false) return;
    setValue("");
  };

  return (
    <div className="flex">
      <span
        className={`flex items-center rounded-l-md border border-r-0 bg-muted px-3 text-sm text-muted-foreground ${disabled ? "opacity-50" : ""}`}
      >
        https://
      </span>
      <Input
        className="rounded-none"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
      />
      <Button
        type="button"
        variant="secondary"
        className="rounded-l-none border border-l-0"
        onClick={submit}
        disabled={disabled}
      >
        {t("onboardingWizard.add")}
      </Button>
    </div>
  );
};

/**
 * Step 3 del wizard custom — "Aggiungi le fonti"
 * (frame 4032:10795, stato vuoto e compilato): sezioni inline per URL
 * pagine web (0/5), sitemap (0/1) e dropzone file (0/10); contatore
 * con check verde #19D163 e input disabilitato a limite raggiunto.
 * Le fonti restano in stato locale fino a "Crea il tuo Bot".
 */
const StepSources = ({
  config,
  updateConfig,
  onBack,
  onCreate,
  isCreating,
  createError,
  // Chi puo' comprare un piano: una fonte oltre il piano apre la scelta del
  // piano invece di fermarsi all'avviso.
  onOltreIlPiano,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileErrors, setFileErrors] = useState([]);

  const urls = config.sources.filter((s) => s.type === "url");
  const sitemaps = config.sources.filter((s) => s.type === "sitemap");
  const files = config.sources.filter(isFileSource);

  // Il limite di fonti del piano: quante ne restano oltre a quelle gia'
  // caricate (anche da altri assistenti) e a quelle scelte qui. Senza dati
  // (o senza limite) valgono solo i limiti delle sezioni.
  const consumi = useBillingUsage();
  const pianoAttuale = consumi.data?.plan;
  const maxFonti = consumi.data?.effective?.limits?.sources_max || 0;
  const liberi = restanti(maxFonti, consumi.data?.usage?.sources_count) - config.sources.length;
  const pianoPieno = maxFonti > 0 && liberi <= 0;
  // A piano pieno chi puo' comprare trova i campi ancora attivi, anche oltre il
  // tetto della sezione: il clic su «Aggiungi» apre la scelta del piano
  // (richiesta dopo la live: i prezzi, non la scritta). Per gli altri resta
  // l'avviso scritto e i campi si spengono.
  const apreIPiani = Boolean(onOltreIlPiano) && pianoPieno;
  // Il limite di un tipo: quello della sezione, ma non oltre i posti del piano.
  // Con la scelta del piano i campi restano attivi: aggiungere apre i piani.
  const limiteDi = (tipo, quante) =>
    onOltreIlPiano ? LIMITS[tipo] : Math.min(LIMITS[tipo], quante + Math.max(0, liberi));
  const nomePiano = !pianoAttuale || pianoAttuale.key === GRATUITO ? t("onboardingWizard.planFree") : pianoAttuale.name;

  const addSource = (source) => {
    if (onOltreIlPiano && pianoPieno) {
      onOltreIlPiano();
      return false;
    }
    updateConfig({ sources: [...config.sources, source] });
  };

  const removeSource = (source) => {
    updateConfig({ sources: config.sources.filter((s) => s !== source) });
  };

  const fileSubtitle = (source) => {
    if (source.type === "pdf") {
      return `${formatSize(source.file?.size)} - ${t("onboardingWizard.fileTypePdf")}`;
    }
    const kind =
      source.fileExt === "md"
        ? t("onboardingWizard.fileTypeMd")
        : t("onboardingWizard.fileTypeTxt");
    return `${formatSize(source.sizeBytes)} - ${kind}`;
  };

  const handleFiles = async (fileList) => {
    const errors = [];
    let fileCount = files.length;
    let disponibili = Math.max(0, liberi);
    const added = [];

    for (const file of Array.from(fileList)) {
      if (disponibili <= 0) {
        if (onOltreIlPiano) onOltreIlPiano();
        else errors.push(t("onboardingWizard.planSourcesFull", { limit: maxFonti, plan: nomePiano }));
        break;
      }
      if (fileCount >= LIMITS.file) {
        errors.push(t("onboardingWizard.fileLimitReached"));
        break;
      }
      if (file.size > MAX_FILE_BYTES) {
        errors.push(`${file.name}: ${t("onboardingWizard.fileTooLarge")}`);
        continue;
      }
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      if (ext === "pdf") {
        added.push({ type: "pdf", file, filename: file.name });
        fileCount++;
        disponibili--;
      } else if (ext === "txt" || ext === "md") {
        // Il backend ingerisce i file solo come PDF: txt/md diventano
        // sorgenti di tipo testo lette client-side.
        const text = await file.text();
        if (text.trim().length < 10) {
          errors.push(`${file.name}: ${t("onboardingWizard.fileTooShort")}`);
          continue;
        }
        added.push({
          type: "text",
          title: file.name,
          inline_text: text.slice(0, MAX_INLINE_TEXT),
          fromFile: true,
          fileExt: ext,
          sizeBytes: file.size,
        });
        fileCount++;
        disponibili--;
      } else {
        errors.push(`${file.name}: ${t("onboardingWizard.docNotSupported")}`);
      }
    }

    if (added.length > 0) {
      updateConfig({ sources: [...config.sources, ...added] });
    }
    setFileErrors(errors);
  };

  const sectionHeader = (label, count, limit, unit) => (
    <div className="flex items-baseline justify-between">
      <Label className="text-sm">{label}</Label>
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {count >= limit && <Check className="h-4 w-4 text-[#19D163]" />}
        {count}/{limit} {unit}
      </span>
    </div>
  );

  const hint = (text) => (
    <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      {text}
    </p>
  );

  return (
    <Card className="rounded-[14px] shadow-[0_5px_5px_rgba(0,0,0,0.1)]">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-xl">
            {t("onboardingWizard.step3Title")}
          </CardTitle>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground">
              {t("onboardingWizard.yourPlan")}
            </span>
            <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-medium text-emerald-800">
              <CheckCircle2 className="h-3 w-3" />
              {nomePiano}
            </span>
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground"
                    aria-label={nomePiano}
                  >
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                {/* Tooltip nero come da Figma (nodo 4032:10909): 205px,
                    raggio 8, testo bianco 12/16 con grassetto e freccia */}
                <TooltipContent
                  side="right"
                  className="w-[205px] rounded-lg border-0 bg-black px-2 py-1.5 text-white shadow-none"
                >
                  <p className="mb-3 text-xs leading-4 tracking-[0.18px]">
                    {maxFonti > 0
                      ? t("onboardingWizard.planTooltipSources", { plan: nomePiano, sources: maxFonti, left: Math.max(0, liberi) })
                      : t("onboardingWizard.planTooltipUnlimited", { plan: nomePiano })}
                  </p>
                  <p className="text-xs leading-4 tracking-[0.18px]">
                    {t("onboardingWizard.planTooltipFooter")}
                  </p>
                  <TooltipPrimitive.Arrow
                    className="fill-black"
                    width={11}
                    height={5}
                  />
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        <CardDescription>{t("onboardingWizard.step3Subtitle")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {createError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {createError}
          </div>
        )}
        {maxFonti > 0 && liberi <= 0 && !onOltreIlPiano && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {t("onboardingWizard.planSourcesFull", { limit: maxFonti, plan: nomePiano })}
          </div>
        )}

        {/* URL pagine web */}
        <div className="space-y-2">
          {sectionHeader(
            t("onboardingWizard.urlPagesLabel"),
            urls.length,
            limiteDi("url", urls.length),
            t("onboardingWizard.linkUnit")
          )}
          <UrlAdder
            placeholder="example.com/page"
            disabled={!apreIPiani && urls.length >= limiteDi("url", urls.length)}
            onAdd={(url) => addSource({ type: "url", url })}
          />
          {urls.length > 0 && (
            <div>
              {urls.map((s, i) => (
                <SourceRow
                  key={`url-${i}`}
                  name={s.url}
                  onRemove={() => removeSource(s)}
                  t={t}
                />
              ))}
            </div>
          )}
          {hint(t("onboardingWizard.urlPagesHint"))}
        </div>

        {/* URL sitemap */}
        <div className="space-y-2">
          {sectionHeader(
            t("onboardingWizard.sitemapLabel"),
            sitemaps.length,
            limiteDi("sitemap", sitemaps.length),
            t("onboardingWizard.linkUnit")
          )}
          <UrlAdder
            placeholder="example.com/sitemap.xml"
            disabled={!apreIPiani && sitemaps.length >= limiteDi("sitemap", sitemaps.length)}
            onAdd={(url) => addSource({ type: "sitemap", url })}
          />
          {sitemaps.length > 0 && (
            <div>
              {sitemaps.map((s, i) => (
                <SourceRow
                  key={`sm-${i}`}
                  name={s.url}
                  onRemove={() => removeSource(s)}
                  t={t}
                />
              ))}
            </div>
          )}
          {hint(t("onboardingWizard.sitemapHint"))}
        </div>

        {/* File e documenti */}
        <div className="space-y-2">
          {sectionHeader(
            t("onboardingWizard.filesLabel"),
            files.length,
            limiteDi("file", files.length),
            t("onboardingWizard.fileUnit")
          )}
          <div
            className={`flex flex-col items-center gap-1.5 rounded-lg border border-dashed px-4 py-8 text-center transition-colors ${
              isDragging ? "border-blue-400 bg-blue-50/50" : "border-border"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              handleFiles(e.dataTransfer.files);
            }}
          >
            <FilePlus2
              className="h-7 w-7 text-muted-foreground"
              strokeWidth={1.5}
            />
            <p className="text-sm">
              {t("onboardingWizard.dropzoneText")}{" "}
              <button
                type="button"
                className="font-semibold underline underline-offset-2"
                onClick={() => fileInputRef.current?.click()}
              >
                {t("onboardingWizard.browseFiles")}
              </button>
            </p>
            <p className="text-xs text-muted-foreground">
              {t("onboardingWizard.dropzoneFormats")}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.txt,.md"
              className="hidden"
              onChange={(e) => {
                handleFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </div>
          {fileErrors.length > 0 && (
            <div className="space-y-0.5 text-xs text-red-600">
              {fileErrors.map((err, i) => (
                <p key={i}>{err}</p>
              ))}
            </div>
          )}
          {files.length > 0 && (
            <div>
              {files.map((s, i) => (
                <SourceRow
                  key={`file-${i}`}
                  name={s.type === "pdf" ? s.filename : s.title}
                  subtitle={fileSubtitle(s)}
                  onRemove={() => removeSource(s)}
                  t={t}
                />
              ))}
            </div>
          )}
        </div>

        {/* Tipologie di file supportati */}
        <div className="rounded-lg border bg-muted/40 p-4">
          <p className="flex items-center gap-2 text-sm font-semibold">
            <Info className="h-4 w-4 shrink-0" />
            {t("onboardingWizard.supportedTypesTitle")}
          </p>
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
            {t("onboardingWizard.supportedTypesDesc")}
          </p>
          <ul className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 pl-5 text-xs leading-relaxed text-muted-foreground sm:grid-cols-2 list-disc">
            <li>{t("onboardingWizard.typePdf")}</li>
            <li>{t("onboardingWizard.typeTxt")}</li>
            <li>{t("onboardingWizard.typeMd")}</li>
          </ul>
        </div>

        <div className="flex items-center justify-between pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onBack}
            disabled={isCreating}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("onboardingWizard.back")}
          </Button>
          <Button type="button" onClick={onCreate} disabled={isCreating}>
            {isCreating ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Bot className="mr-2 h-4 w-4" />
            )}
            {isCreating
              ? t("onboardingWizard.creatingBot")
              : t("onboardingWizard.createBot")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default StepSources;
